// Chronological course-send activity log. Mirrors SendHistoryPage but for
// COURSE_SEND_LOG. Scope filters apply user-level dimensions (department,
// location, etc.) to the rows before display; sorting and pagination are
// in-memory.
//
// Calculations: filter COURSE_SEND_LOG by scope (user metadata), sort by
// dateSent DESC. Completion lag column = dateCompleted - dateSent, or
// 'Pending' when dateCompleted is null.
//
// Postgres mapping:
//   - source table `course_sends` (send_id, user_id, course_id, sent_at,
//     source, completed_at) joined to `users` for dept/location.
//   - paginated query with server-side WHERE for scope filters.
//
// New data points: send `source` enum (Journey Builder | On-Demand | ...)
// must become a column; today it is a derived string.
//
// Integration hook: `useCourseHistoryQuery({ startDate, endDate,
// userEmail, status, courseName, limit, offset })` cache 10 minutes.
import React, { useMemo } from 'react';
import { Box, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { COURSE_SEND_LOG } from '../data';
import useDashboardData from '../hooks/useDashboardData';
import ActiveFilters from './ActiveFilters';
import DataTable from './DataTable';

const isIn = (val, vs) => { if (!vs) return true; const a = Array.isArray(val) ? val : [val]; return Array.isArray(vs) ? a.some((x) => vs.includes(x)) : a.includes(vs); };

function applyScopeToCourseLog(log, scope) {
    const s = scope || {};
    return log.filter((e) => {
        if (!isIn(e.dept,       s.department)) return false;
        if (!isIn(e.location,   s.location))   return false;
        if (!isIn(e.city,       s.city))       return false;
        if (!isIn(e.country,    s.country))    return false;
        if (!isIn(e.company,    s.company))    return false;
        if (!isIn(e.state,      s.state))      return false;
        if (!isIn(e.adGroups,    s.adGroup))    return false;
        if (!isIn(e.courseName, s.course))     return false;
        if (s.user) {
            const userVals = Array.isArray(s.user) ? s.user : [s.user];
            const full = `${e.firstName} ${e.lastName}`;
            if (!userVals.some((v) => e.email === v || full === v)) return false;
        }
        return true;
    });
}

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

function fmtDate(str) {
    if (!str) return ', ';
    return new Date(str).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });
}

function StatusBadge({ completed }) {
    return completed
        ? <Box sx={{ display: 'inline-flex', px: '8px', py: '2px', borderRadius: '999px', fontSize: 10, fontWeight: 600, bgcolor: 'var(--green-bg)', color: 'var(--green-text)', border: '1px solid rgba(102,187,106,.25)' }}>Completed</Box>
        : <Box sx={{ display: 'inline-flex', px: '8px', py: '2px', borderRadius: '999px', fontSize: 10, fontWeight: 600, bgcolor: 'var(--amber-bg)', color: 'var(--amber-text)', border: '1px solid rgba(255,202,40,.3)' }}>Outstanding</Box>;
}

function exportCSV(rows) {
    const headers = ['Name', 'Email', 'Course', 'Department', 'Location', 'Date Sent', 'Date Completed', 'Status'];
    const lines = [headers.join(','), ...rows.map((r) => [
        `"${r.firstName} ${r.lastName}"`, `"${r.email}"`, `"${r.courseName}"`,
        `"${r.dept}"`, `"${r.location}"`,
        `"${r.dateSent || ''}"`, `"${r.dateCompleted || ''}"`,
        `"${r.dateCompleted ? 'Completed' : 'Outstanding'}"`,
    ].join(','))];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'course-send-log.csv'; a.click();
    URL.revokeObjectURL(url);
}

export default function CourseSendLogPage({ drill, scope, removeScope }) {
    const rows = useMemo(() => applyScopeToCourseLog(COURSE_SEND_LOG, scope), [scope]);
    // True total of course delivery events (uncapped) from the course rollup; the raw
    // event pull is capped at the most-recent 20k, so show the real total instead.
    const totalQ = useDashboardData({ metric: 'courses', aggregation: 'courses', startDate: '2015-01-01', endDate: '2099-12-31' });
    const trueTotal = Array.isArray(totalQ.data?.courses) ? totalQ.data.courses.reduce((a, r) => a + (r.sent || 0), 0) : null;

    const columns = [
        { id: 'name',          label: 'Name',           always: true,    sortable: true, type: 'string', accessor: (r) => `${r.firstName} ${r.lastName}` },
        { id: 'email',         label: 'Email',          defaultOn: true, sortable: true, type: 'string', accessor: (r) => r.email },
        { id: 'courseName',    label: 'Course',         defaultOn: true, sortable: true, type: 'string', accessor: (r) => r.courseName },
        { id: 'dept',          label: 'Department',     defaultOn: true, sortable: true, type: 'string', accessor: (r) => r.dept },
        { id: 'location',      label: 'Location',       defaultOn: true, sortable: true, type: 'string', accessor: (r) => r.location },
        { id: 'dateSent',      label: 'Sent',           defaultOn: true, sortable: true, type: 'string', accessor: (r) => r.dateSent || '', format: (v) => fmtDate(v) },
        { id: 'dateCompleted', label: 'Completed',      defaultOn: true, sortable: true, type: 'string', accessor: (r) => r.dateCompleted || '', format: (v) => fmtDate(v) },
        { id: 'status',        label: 'Status',         defaultOn: true, sortable: true, type: 'string', accessor: (r) => r.dateCompleted ? 'Completed' : 'Outstanding',
          render: (r) => <StatusBadge completed={!!r.dateCompleted} /> },
    ];

    return (
        <motion.div variants={stagger} initial="hidden" animate="visible" style={{ padding: '0 24px 48px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <ActiveFilters scope={scope} removeScope={removeScope} noPadX />
            <motion.div variants={fadeUp}>
                <DataTable
                    title="Course Send Log"
                    subtitle={(() => {
                        const cap = COURSE_SEND_LOG.length;
                        if (trueTotal != null && trueTotal > cap) return `${rows.length.toLocaleString()} shown \u2014 most recent ${cap.toLocaleString()} of ${trueTotal.toLocaleString()} events (newest first)`;
                        return `${rows.length.toLocaleString()} of ${COURSE_SEND_LOG.length.toLocaleString()} course delivery events${rows.length !== COURSE_SEND_LOG.length ? ' (scoped)' : ''}`;
                    })()}
                    columns={columns}
                    rows={rows}
                    rowKey={(r) => `${r.email}-${r.courseName}-${r.dateSent}`}
                    onRowClick={(r) => drill('user-detail', `${r.firstName} ${r.lastName}`, { type: 'user', value: r.email })}
                    initialSort={{ by: 'dateSent', dir: 'desc' }}
                    storageKey="rbx-course-send-log-cols"
                    emptyMessage="No matching course deliveries"
                    headerActions={
                        <Button
                            onClick={() => exportCSV(rows)}
                            variant="outlined" size="small"
                            sx={{ borderRadius: '4px', borderColor: 'secondary.main', color: 'secondary.main', fontSize: 11, textTransform: 'none', whiteSpace: 'nowrap', '&:hover': { bgcolor: 'secondary.main', color: 'common.white' } }}
                        >
                            Export CSV ({rows.length})
                        </Button>
                    }
                />
            </motion.div>
        </motion.div>
    );
}
